// Who is doing the warm-up.
//
// A signed-in visitor uses the web session JWT the gateway already accepts
// (issuer 'baba_web'). Everyone else gets the same anonymous identity the
// phone apps use: a device token minted by the gateway from a device secret
// this browser keeps to itself. The free daily warm-up is the product's
// on-ramp, so it must not sit behind a sign-in wall — and neither should the
// judge's evaluation data, which only accrues when people actually use it.
import { getAuthToken, getInstallationId } from '../baba-auth'
import { VOICE_BASE_URL } from '../realtime/usage'

const DEVICE_SECRET_KEY = 'baba.drill.deviceSecret'
const STABLE_ID_KEY = 'baba.drill.stableUserId'
const DEVICE_TOKEN_KEY = 'baba.drill.deviceToken'
const DEVICE_TOKEN_AT_KEY = 'baba.drill.deviceTokenAt'
/** Gateway tokens live 30 days; re-mint well before that. */
const TOKEN_MAX_AGE_MS = 20 * 24 * 60 * 60 * 1000

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

function randomHex(bytes: number): string {
  const buf = new Uint8Array(bytes)
  crypto.getRandomValues(buf)
  return Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('')
}

function ensure(key: string, make: () => string): string {
  const s = storage()
  const existing = s?.getItem(key)
  if (existing) return existing
  const fresh = make()
  s?.setItem(key, fresh)
  return fresh
}

export interface DrillIdentity {
  token: string
  kind: 'web' | 'device'
}

async function mintDeviceToken(fetchImpl: typeof fetch = fetch): Promise<string> {
  const deviceSecret = ensure(DEVICE_SECRET_KEY, () => randomHex(32))
  const stableUserId = ensure(STABLE_ID_KEY, () => crypto.randomUUID())
  const res = await fetchImpl(`${VOICE_BASE_URL}/api/device-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ deviceSecret, deviceId: getInstallationId(), stableUserId, platform: 'web' }),
  })
  if (!res.ok) throw new Error(`Could not start an anonymous session (${res.status})`)
  const body = (await res.json()) as { token?: string }
  if (!body.token) throw new Error('Could not start an anonymous session')
  const s = storage()
  s?.setItem(DEVICE_TOKEN_KEY, body.token)
  s?.setItem(DEVICE_TOKEN_AT_KEY, String(Date.now()))
  return body.token
}

/** The bearer token to use for drill calls. Mints an anonymous one when needed. */
export async function resolveDrillIdentity(fetchImpl: typeof fetch = fetch): Promise<DrillIdentity> {
  const web = getAuthToken()
  if (web) return { token: web, kind: 'web' }
  const s = storage()
  const cached = s?.getItem(DEVICE_TOKEN_KEY)
  const at = Number(s?.getItem(DEVICE_TOKEN_AT_KEY) ?? 0)
  if (cached && Date.now() - at < TOKEN_MAX_AGE_MS) return { token: cached, kind: 'device' }
  return { token: await mintDeviceToken(fetchImpl), kind: 'device' }
}

/** Drop a cached device token (after a 401) so the next call re-mints. */
export function forgetDrillDeviceToken(): void {
  const s = storage()
  s?.removeItem(DEVICE_TOKEN_KEY)
  s?.removeItem(DEVICE_TOKEN_AT_KEY)
}
